import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class DataService {
  serverUrl: string;

  constructor() {
    this.serverUrl = environment.serverRoot;
  }

  agreementsUrl(agreementId?: string): string {
    let url = `${this.serverUrl}/agreements`;
    if (agreementId) {
      url += `/${agreementId}`;
    }
    return url;
  }

  agreementsUnsignedForUserUrl(userId: string): string {
    return `${this.agreementsUrl()}/unsigned/user/${userId}`;
  }

  downloadsUrl(): string {
    return `${this.serverUrl}/downloads`;
  }

  downloadsByReleaseTagUrl(releaseTag: string): string {
    return `${this.downloadsUrl()}/release-tag/${releaseTag}`;
  }

  downloadsByTypeUrl(type: string): string {
    return `${this.downloadsUrl()}/type/${type}`;
  }

  institutionsUrl(institutionId?: string): string {
    let url = `${this.serverUrl}/institutions`;
    if (institutionId) {
      url += `/${institutionId}`;
    }
    return url;
  }

  activateInstitutionUrl(institutionId: string): string {
    return `${this.institutionsUrl(institutionId)}/activate`;
  }

  institutionReleaseTagsUrl(): string {
    return `${this.serverUrl}/institution-release-tags`;
  }

  institutionReleaseTagsForInstitutionUrl(institutionId: string): string {
    return `${this.institutionReleaseTagsUrl()}/institution/${institutionId}`;
  }

  institutionReleaseTagsForReleaseTagUrl(releaseTagId: string): string {
    return `${this.institutionReleaseTagsUrl()}/release-tag/${releaseTagId}`;
  }

  institutionScenarioPackagesUrl(): string {
    return `${this.serverUrl}/institution-scenario-packages`;
  }

  institutionScenarioPackagesForInstitutionUrl(institutionId: string): string {
    return `${this.institutionScenarioPackagesUrl()}/institution/${institutionId}`;
  }

  institutionScenarioPackagesForScenarioPackageUrl(
    scenarioPackageId: string,
  ): string {
    return `${this.institutionScenarioPackagesUrl()}/scenario-package/${scenarioPackageId}`;
  }

  institutionUsersUrl(): string {
    return `${this.serverUrl}/institution-users`;
  }

  institutionUsersForInstitutionUrl(institutionId: string): string {
    return `${this.institutionUsersUrl()}/institution/${institutionId}`;
  }

  institutionUsersForUserUrl(userId: string): string {
    return `${this.institutionUsersUrl()}/user/${userId}`;
  }

  loginPortalUrl(): string {
    return `${this.serverUrl}/login/portal-new`;
  }

  permissionsUrl(): string {
    return `${this.serverUrl}/permissions`;
  }

  releaseTagsUrl(): string {
    return `${this.serverUrl}/release-tags`;
  }

  reportingCodesUrl(reportingCodeId?: string): string {
    let url = `${this.serverUrl}/reporting-codes`;
    if (reportingCodeId) {
      url += `/${reportingCodeId}`;
    }
    return url;
  }

  rolesUrl(): string {
    return `${this.serverUrl}/roles`;
  }

  scenariosUrl(scenarioId?: string): string {
    let url = `${this.serverUrl}/scenarios`;
    if (scenarioId) {
      url += `/${scenarioId}`;
    }
    return url;
  }

  scenarioDocumentsUrl(scenarioDocumentId?: string): string {
    let url = `${this.serverUrl}/scenario-documents`;
    if (scenarioDocumentId) {
      url += `/${scenarioDocumentId}`;
    }
    return url;
  }

  scenarioDocumentsForScenarioUrl(scenarioId: string): string {
    return `${this.scenarioDocumentsUrl()}/scenario/${scenarioId}`;
  }

  scenarioPackagesUrl(scenarioPackageId?: string): string {
    let url = `${this.serverUrl}/scenario-packages`;
    if (scenarioPackageId) {
      url += `/${scenarioPackageId}`;
    }
    return url;
  }

  scenarioPackageItemsUrl(scenarioPackageItemId?: string): string {
    let url = `${this.serverUrl}/scenario-package-items`;
    if (scenarioPackageItemId) {
      url += `/${scenarioPackageItemId}`;
    }
    return url;
  }

  scenarioPackageItemsForScenarioPackageUrl(scenarioPackageId: string): string {
    return `${this.scenarioPackageItemsUrl()}/scenario-package/${scenarioPackageId}`;
  }

  scenarioReleaseTagsUrl(scenarioReleaseTagId?: string): string {
    let url = `${this.serverUrl}/scenario-release-tags`;
    if (scenarioReleaseTagId) {
      url += `/${scenarioReleaseTagId}`;
    }
    return url;
  }

  scenarioReleaseTagsForReleaseTagUrl(releaseTagId: string): string {
    return `${this.scenarioReleaseTagsUrl()}/release-tag/${releaseTagId}`;
  }

  scenarioReleaseTagsForScenarioUrl(scenarioId: string): string {
    return `${this.scenarioReleaseTagsUrl()}/scenario/${scenarioId}`;
  }

  scenarioReleasesUrl(scenarioReleaseId?: string): string {
    let url = `${this.serverUrl}/scenario-releases`;
    if (scenarioReleaseId) {
      url += `/${scenarioReleaseId}`;
    }
    return url;
  }

  scenarioReleasesForScenarioUrl(scenarioId: string): string {
    return `${this.scenarioReleasesUrl()}/scenario/${scenarioId}`;
  }

  scenarioReleaseFileUrl(scenarioReleaseId: string): string {
    return `${this.scenarioReleasesUrl(scenarioReleaseId)}/file`;
  }

  scenarioRevisionsUrl(scenarioRevisionId?: string): string {
    let url = `${this.serverUrl}/scenario-revisions`;
    if (scenarioRevisionId) {
      url += `/${scenarioRevisionId}`;
    }
    return url;
  }

  scenarioRevisionsForScenarioUrl(scenarioId: string): string {
    return `${this.scenarioRevisionsUrl()}/scenario/${scenarioId}`;
  }

  scenarioRevisionFileUrl(scenarioRevisionId: string): string {
    return `${this.scenarioRevisionsUrl(scenarioRevisionId)}/file`;
  }

  simSessionsUrl(simSessionId?: string): string {
    let url = `${this.serverUrl}/sim-sessions`;
    if (simSessionId) {
      url += `/${simSessionId}`;
    }
    return url;
  }

  simSessionsForReportingCodeUrl(reportingCodeId: string): string {
    return `${this.simSessionsUrl()}/reporting-code/${reportingCodeId}`;
  }

  simSessionsForReportingCodeAndSessionIdUrl(
    reportingCodeCode: string,
    simSessionSessionId: string,
  ): string {
    return `${this.simSessionsUrl()}/reporting-code/${reportingCodeCode}/session-id/${simSessionSessionId}`;
  }

  simSessionReportUrl(simSessionId: string): string {
    return `${this.simSessionsUrl(simSessionId)}/report`;
  }

  tagsUrl(taxonomy?: string): string {
    let url = `${this.serverUrl}/tags`;
    if (taxonomy) {
      url += `/${taxonomy}`;
    }
    return url;
  }

  taxonomiesUrl(taxonomy?: string): string {
    let url = `${this.serverUrl}/taxonomies`;
    if (taxonomy) {
      url += `/${taxonomy}`;
    }
    return url;
  }

  usersUrl(userId?: string): string {
    let url = `${this.serverUrl}/users`;
    if (userId) {
      url += `/${userId}`;
    }
    return url;
  }

  updateUserPasswordUrl(userId: string): string {
    return `${this.usersUrl(userId)}/password`;
  }

  resetUserPasswordUrl(): string {
    return `${this.usersUrl()}/password/reset`;
  }

  userAgreementsUrl(userAgreementId?: string): string {
    let url = `${this.serverUrl}/user-agreements`;
    if (userAgreementId) {
      url += `/${userAgreementId}`;
    }
    return url;
  }

  userAgreementsForAgreementUrl(agreementId: string): string {
    return `${this.userAgreementsUrl()}/agreement/${agreementId}`;
  }

  userAgreementsForUserUrl(userId: string): string {
    return `${this.userAgreementsUrl()}/user/${userId}`;
  }

  validateTokenUrl(): string {
    return `${this.serverUrl}/validate-token`;
  }
}
